'use strict';

import { useEffect, useMemo, useRef, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { ClearRefinements, Configure, Highlight, InstantSearch, Pagination, RefinementList, SearchBox, useHits, useInstantSearch } from 'react-instantsearch';
import algoliasearch from 'algoliasearch/lite';

export const WebinarSearch = ({ config }) => {
	const { id, key, index } = config;
	const client = useMemo(() => algoliasearch(id, key), [id, key]);
	const [now] = useState(Date.now);
	
	return (
		<InstantSearch indexName={ index } searchClient={ client } future={ { preserveSharedStateOnUnmount: true } }>
			{ /* is_free lives in `filters` because refining a facet replaces any configured facetFilters */ }
			<Configure filters={ `broadcast_at < ${ now } AND is_free:true` } />
			<ScrollTo>
				<div className="mb-6">
					<h3 className="m-0 mb-2 text-base font-condensed">
						Browse &amp; Search Past Recordings
					</h3>
					
					<SearchBox autoFocus={ true } placeholder="Search here…" />
				</div>
				
				<div className="flex flex-wrap -mx-3 webinars">
					
					<div className="px-3 w-full md:w-3/4">
						<ClearRefinements excludedAttributes={ [] } translations={ { resetButtonText: 'Clear all filters' } } />
						
						<Hits />
						
						<Pagination className="my-12" showLast={ false } translations={ { firstPageItemText: '«' } } />
					</div>
					
					<div className="hidden px-3 md:block md:w-1/4">
						<div className="pl-3 h-full">
							
							<h3 className="mt-4 text-base font-condensed">
								Filter by Category
							</h3>
							
							<RefinementList attribute="categories.title" />
							
							<h3 className="mt-4 text-base font-condensed">
								Filter by Length
							</h3>
							
							<RefinementList attribute="length" />
							
							<h3 className="mt-4 text-base font-condensed">
								Filter by Person
							</h3>
							
							<RefinementList attribute="people.title" />
							
						</div>
					</div>
					
				</div>
			</ScrollTo>
		</InstantSearch>
	);
};

// Replaces v6's <ScrollTo>, which v7 dropped: bring the results back into view when the page changes
const ScrollTo = ({ children }) => {
	const ref = useRef(null);
	const { indexUiState } = useInstantSearch();
	const previous_page = useRef(indexUiState.page);
	
	useEffect(() => {
		if (previous_page.current !== indexUiState.page) {
			ref.current.scrollIntoView();
		}
		
		previous_page.current = indexUiState.page;
	}, [indexUiState.page]);
	
	return <div className="ais-ScrollTo" ref={ ref }>{ children }</div>;
};

const Hits = () => {
	const { items } = useHits();
	
	return (
		<div className="flex flex-wrap items-stretch -m-2">
			{ items.map(hit => <HitCard key={ hit.objectID } hit={ hit } />) }
		</div>
	);
};

const HitCard = ({ hit }) => {
	return (
		<a href={ hit.url } className="p-2 w-full no-underline cursor-pointer lg:w-1/2 group">
			<div className="flex overflow-hidden flex-col h-full rounded-lg border border-gray-200 shadow group-hover:border-blue-400">
				<div className="relative h-48 bg-gray-200" style={ { clipPath: `polygon(0 0, 100% 0, 100% 100%, 0 95%)` } }>
					<img
						src={ hit.thumbnail }
						className="object-cover w-full h-full"
					/>
				</div>
				
				<div className="flex flex-col flex-1 p-6">
					<h2 className="m-0 mb-1 text-lg text-blue-800 group-hover:text-blue-500 group-hover:underline font-condensed">
						<Highlight hit={ hit } attribute="title" highlightedTagName="em" />
					</h2>
					
					<p className="order-3 mt-2 text-sm text-gray-500">
						<Highlight hit={ hit } attribute="description" highlightedTagName="em" />
					</p>
					
					<div className="flex flex-wrap order-4 -mx-1 mt-auto text-xs">
						{ hit.categories.map(category => (
							<div className="m-1 text-blue-700 whitespace-nowrap bg-blue-100 pill" key={ category.slug }>
								{ category.title }
							</div>
						)) }
					</div>
				</div>
			</div>
		</a>
	);
};

const element = document.getElementById('app');

if (element) {
	const config = {
		index: element.dataset.algoliaIndex,
		key: element.dataset.algoliaKey,
		id: element.dataset.algoliaAppId,
	};
	
	createRoot(element).render(<WebinarSearch config={ config } />);
}
